import { FieldArray, Formik, useFormikContext } from "formik";
import { useEffect, useState } from "react";
import apiRequest from "../../../utils/admin/apiRequest";
import InputField from "../../ui/form/InputField";
import Button from "../../ui/utils/Button";
import SelectField from "../../ui/form/SelectField";
import usePost from "../../../hooks/api/usePost";
import Alert from "../../ui/utils/Alert";
import useFetch from "../../../hooks/api/useFetch";
import { VendorStaffUpdateValidationSchema } from "../../../validation/admin/vendoValidation";
import AsyncSearchSelectProfile from "../../ui/form/AsyncSearchSelectProfile";
import FileInputField from "../../ui/form/FileInputField";
import CheckboxField from "../../ui/form/CheckboxField";


// -----------------------------------------
// LOCATION WATCHER
// -----------------------------------------

const LocationWatcher = ({
  fetchStates,
  fetchCities,
  setBlankResponse,
  setCityBlankResponse,
}) => {
  const { values, setFieldValue } = useFormikContext();

  useEffect(() => {
    if (values.countryId) {
      fetchStates({ countryId: values.countryId });

      if (!values.stateId) {
        setFieldValue("stateId", "");
      }
    } else {
      setBlankResponse();
    }
  }, [values.countryId]);

  useEffect(() => {
    if (values.stateId) {
      fetchCities({ stateId: values.stateId });

      if (!values.cityId) {
        setFieldValue("cityId", "");
      }
    } else {
      setCityBlankResponse();
    }
  }, [values.stateId]);

  return null;
};

const CorporateWatcher = ({ fetchCorporateVendors, resetVendors }) => {
  const { values, setFieldValue } = useFormikContext();
  const [lastCorporateId, setLastCorporateId] = useState(null);

  useEffect(() => {
    const corporateId = values.corporateCodeId?.[0];
    if (corporateId && lastCorporateId === null) {
      fetchCorporateVendors({ corporateCodeId: corporateId });
      setLastCorporateId(corporateId);
      return;
    }

    if (corporateId && corporateId !== lastCorporateId) {
      fetchCorporateVendors({ corporateCodeId: corporateId });
      setFieldValue("addedBy", ""); 
      setLastCorporateId(corporateId);
    }

    if (!corporateId) {
      resetVendors();
      setFieldValue("addedBy", "");
      setLastCorporateId(null);
    }
  }, [values.corporateCodeId?.[0]]);

  return null;
};





// =====================================================================
// MAIN COMPONENT
// =====================================================================
const EditVendorStaff = ({ data: vendor, closeModal, callback }) => {
  const [preview, setPreview] = useState(null);

  const { error, isLoading, post, serverErrors } = usePost(
    apiRequest.editVendor
  );

  // ---------------- LOAD DATA ------------------
  const { data: adminList } = useFetch({
    apiMethod: apiRequest.getHeadAdmin,
    body: { paginate: false },
    autoFetch: true,
  });

  const [selectedCorporateOptions, setSelectedCorporateOptions] = useState([]);


  const { data: countryData } = useFetch({
    apiMethod: apiRequest.getCountry,
    body: { paginate: false },
  });

  const { data: stateData, refetch: fetchStates, setBlankResponse } = useFetch({
    apiMethod: apiRequest.getStateByCountryId,
    autoFetch: false,
  });

  const {
    data: cityData,
    refetch: fetchCities,
    setBlankResponse: setCityBlankResponse,
  } = useFetch({
    apiMethod: apiRequest.getCityByState,
    autoFetch: false,
  });


  const initialValues = {
    name: vendor.name || "",
    countryId: vendor?.countryId?._id || "",
    stateId: vendor.stateId?._id || "",
    cityId: vendor.cityId?._id || "",
    mobile: vendor.mobile || "",
    email: vendor.email || "",
    contactPerson: vendor.contactPerson || "",
    userId: vendor.userId || "",
    displayName: vendor.displayName || "",
    password: "",
    vendorType: "vendor",
    addedBy: vendor?.addedBy,
    isSuperVendor: false,
    image: null,
    corporateCodeId: vendor?.corporateCodeId?.length
      ? vendor.corporateCodeId.map((c) => c._id)
      : [""],
  };


  useEffect(() => {
    if (vendor?.corporateCodeId?.length) {
      setSelectedCorporateOptions(
        vendor.corporateCodeId.map((c) => ({
          label: `${c.corporateName} (${c.corporateCode})`,
          value: c._id,
        }))
      );
    }
  }, [vendor]);


  const fetchCorporateCodes = async (search) => {
    const res = await apiRequest.getCorporateCodeForSelect({
      body: { filters: { name: { $regex: search, $options: "i" }, }, paginate: false, },
    });
    return (res?.data?.data?.corporatecodes?.map((v) => ({
      label: `${v.name} (${v.code})`,
      value: v.id,
    })) || []);
  };


  const { data: corporateCodes } = useFetch({
    apiMethod: apiRequest.getCorporateCodeForSelect,
    body: { paginate: false },
  });

  const {
    data: vendors,
    refetch: fetchCorporateVendors,
    setBlankResponse: resetVendors,
  } = useFetch({
    apiMethod: apiRequest.getCorporateCodeByVendorId,
    autoFetch: false,
  });


  return (
    <div>
      <h2 className="p-4 text-lg font-semibold">Edit Vendor Manager Profile</h2>
      <div className="border-b" />

      <Formik
        initialValues={initialValues}
        enableReinitialize
        validationSchema={VendorStaffUpdateValidationSchema}
        onSubmit={async (values, { resetForm }) => {


          const response = await post({ data: { body: values, id: vendor?._id } });

          if (response.response) {
            callback?.();
            resetForm();
            closeModal();
          }
        }}
      >
        {({ values, handleChange, handleSubmit, handleBlur, touched, setFieldValue, errors }) => (
          <>
            <LocationWatcher
              fetchStates={fetchStates}
              fetchCities={fetchCities}
              setBlankResponse={setBlankResponse}
              setCityBlankResponse={setCityBlankResponse}
            />
            <CorporateWatcher fetchCorporateVendors={fetchCorporateVendors} resetVendors={resetVendors} />
            <form onSubmit={handleSubmit} className="text-sm max-h-[80vh] overflow-auto">
              {error && <Alert message={error} type="error" />}

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 p-4">

                <InputField name="name" label="Name" value={values.name} onChange={handleChange} />
                <InputField name="mobile" label="Mobile" value={values.mobile} onChange={handleChange} />
                <InputField
                  name="displayName"
                  label="Display Name"
                  value={values.displayName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.displayName && errors.displayName}
                />
                <InputField name="email" label="Email" value={values.email} onChange={handleChange} />

                <SelectField
                  name="countryId"
                  value={values.countryId}
                  label="Country"
                  onChange={handleChange}
                  options={countryData?.countries?.map((c) => ({ value: c._id, label: c.name }))}
                />

                <SelectField
                  name="stateId"
                  value={values.stateId}
                  label="State"
                  onChange={handleChange}
                  options={stateData?.states?.map((s) => ({ value: s._id, label: s.name }))}
                />

                <SelectField
                  name="cityId"
                  value={values.cityId}
                  label="City"
                  onChange={handleChange}
                  options={cityData?.cities?.map((c) => ({ value: c._id, label: c.name }))}
                />

                <InputField name="contactPerson" label="Contact Person" value={values.contactPerson} onChange={handleChange} />

                <InputField name="userId" label="User ID" value={values.userId} onChange={handleChange} />

                <InputField type="password" name="password" label="Password (optional)" value={values.password} onChange={handleChange} />

                <div className="">
                  <FileInputField
                    error={touched.image && errors.image}
                    id="image"
                    label="Company Logo Upload"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.currentTarget.files?.[0] || null;
                      setFieldValue("image", file);
                      setPreview(file ? URL.createObjectURL(file) : null);
                    }}
                  />
                  {preview && (
                    <img
                      src={preview}
                      alt="Preview"
                      className="object-contain w-20 h-20 mt-2 border rounded"
                    />
                  )}
                  <CheckboxField
                    checked={values.removeBg}
                    name="removeBg"
                    label=" Remove Background"
                    id="removeBg"
                    value={values.removeBg}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    serverError={serverErrors?.removeBg}
                    error={touched.removeBg && errors.removeBg}
                  />
                </div>


                    <SelectField
                      name="corporateCodeId"
                      label="Corporate Code"
                      value={values.corporateCodeId[0] || ""}
                      onChange={(e) => {
                        const value = e?.target?.value;
                        setFieldValue("corporateCodeId", [value]);
                      }}
                      error={
                        touched.corporateCodeId?.[0] &&
                        errors.corporateCodeId?.[0]
                      }
                      options={corporateCodes?.corporatecodes?.map((c) => ({
                        value: c.id,
                        label: `${c.name} (${c.code})`,
                      }))}
                    />




                    {/* Vendor */}
                    <SelectField
                      name="addedBy"
                      label="Vendor"
                      value={values.addedBy}
                      onChange={handleChange}
                      error={touched.addedBy && errors.addedBy}
                      options={vendors?.map((v) => ({
                        value: v._id,
                        label: v.name,
                      }))}
                    />

                
                   




                

                <div className="col-span-full hidden mt-2">
                  <FieldArray name="corporateCodeId">
                    {({ push, remove }) => (
                      <>
                        {values.corporateCodeId.map((_, index) => (
                          <div
                            key={index}
                            className="flex items-center gap-2 mb-2"
                          >
                            <AsyncSearchSelectProfile
                              placeholder="Select Corporate Code"
                              value={selectedCorporateOptions[index] || null}
                              fetchOptions={fetchCorporateCodes}
                              onChange={(option) => {
                                setFieldValue(`corporateCodeId.${index}`, option?.value || "");

                                const updated = [...selectedCorporateOptions];
                                updated[index] = option;
                                setSelectedCorporateOptions(updated);
                              }}
                            />


                            {/* <button
                              type="button"
                              onClick={() => {
                                remove(index);
                                setSelectedCorporateOptions((prev) =>
                                  prev.filter((_, i) => i !== index)
                                );
                              }}
                              className="text-red-500"
                            >
                              <Trash size={16} />
                            </button> */}
                          </div>
                        ))}

                        {/* <button
                          type="button"
                          onClick={() => {
                            push("");
                            setSelectedCorporateOptions((prev) => [
                              ...prev,
                              null,
                            ]);
                          }}
                          className="mt-2 px-2 py-1 bg-blue-500 text-white text-xs rounded flex items-center gap-2"
                        >
                          <CgAdd /> Add Corporate Code
                        </button> */}
                      </>
                    )}
                  </FieldArray>
                </div>
              </div>

              {/* -------------- SUBMIT ---------------- */}
              <div className="flex justify-end gap-2 border-t p-4">
                <Button variant="secondary" onClick={closeModal}>
                  Cancel
                </Button>
                <Button type="submit" variant="primary" isLoading={isLoading}>
                  Update Vendor Staff
                </Button>
              </div>
            </form>
          </>
        )}
      </Formik>
    </div>
  );
};

export default EditVendorStaff;
